import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, MessageCircle, Home, Check, Bed, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import PageMeta from '../PageMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Public listings index =====
export const PublicListingsIndex = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/listings/public`)
      .then(r => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Breadcrumb schema for listings index
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", position: 1, name: "RentKollect", item: "https://rentkollect.com/" },
        { "@type": "ListItem", position: 2, name: "Listings", item: "https://rentkollect.com/listings" },
      ],
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = 'schema-listings-breadcrumb';
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <PageMeta
        title="Browse PGs & Hostels — Verified listings"
        description="Find verified PG, hostel and co-living options listed by RentKollect owners. Real-time bed availability, transparent rent, and direct WhatsApp contact."
        canonical="https://rentkollect.com/listings"
      />
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-blue-700">
            <Home /> RentKollect
          </Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-blue-600">Back to Home →</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="public-listings-page">
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">Find Your Next Home</h1>
          <p className="text-gray-600 text-lg">Handpicked co-living spaces across India</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Home size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-xl text-gray-700">No listings available right now</p>
            <p className="text-gray-500 mt-2">Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="public-listings-grid">
            {items.map(it => (
              <Link
                key={it.property_id}
                to={`/listings/${it.property_id}`}
                data-testid={`public-listing-${it.property_id}`}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all border border-gray-100 hover:-translate-y-1"
              >
                <div className="aspect-video bg-gradient-to-br from-blue-400 to-indigo-500 relative">
                  {it.cover_image ? (
                    <img src={it.cover_image} alt={it.property_name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/80">
                      <Home size={64} />
                    </div>
                  )}
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full text-xs font-bold text-green-700">
                    {it.vacant_rooms} vacant
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-bold text-gray-900 text-lg group-hover:text-blue-700 transition-colors">{it.property_name}</h3>
                  {it.address && (
                    <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                      <MapPin size={12} /> {it.address}{it.city ? `, ${it.city}` : ''}
                    </p>
                  )}
                  {it.headline && <p className="text-sm text-gray-700 italic mt-2">"{it.headline}"</p>}
                  <div className="flex gap-1 flex-wrap mt-3">
                    {(it.highlights || []).slice(0, 3).map((h, i) => (
                      <span key={i} className="text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full">{h}</span>
                    ))}
                  </div>
                  {it.starting_rent && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500">Starting from</p>
                      <p className="text-2xl font-bold text-blue-700">₹{it.starting_rent.toLocaleString()}/mo</p>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ===== Public single listing =====
export const PublicListingDetail = () => {
  const { propertyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showInquiry, setShowInquiry] = useState(false);

  useEffect(() => {
    axios.get(`${API}/listings/public/${propertyId}`)
      .then(r => setData(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [propertyId]);

  const property = data?.property;
  const settings = data?.settings;
  const available_rooms = data?.available_rooms;
  const availableBeds = (available_rooms || []).reduce((a, r) => a + ((r.total_beds || 0) - (r.occupied_beds || 0)), 0);
  const minRent = Math.min(...(available_rooms || []).map(r => r.rent_per_bed || Infinity));
  const rentText = Number.isFinite(minRent) ? `from ₹${minRent.toLocaleString('en-IN')}/bed/month` : 'best rates';

  // Inject BreadcrumbList + LodgingBusiness schemas so search engines and
  // AI answer engines (ChatGPT, Claude, Perplexity) can represent this PG
  // as a rich entity with location, pricing, and amenities.
  useEffect(() => {
    if (!property) return;
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", position: 1, name: "RentKollect", item: "https://rentkollect.com/" },
          { "@type": "ListItem", position: 2, name: "Listings", item: "https://rentkollect.com/listings" },
          { "@type": "ListItem", position: 3, name: property.name, item: `https://rentkollect.com/listings/${property.id}` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        "@id": `https://rentkollect.com/listings/${property.id}#lodging`,
        "name": property.name,
        "description": settings?.tagline || `${property.name} in ${property.city || 'India'} — verified PG listing on RentKollect.`,
        "url": `https://rentkollect.com/listings/${property.id}`,
        "image": settings?.cover_image || property.images?.[0] || undefined,
        "telephone": property.contact_phone ? `+91-${property.contact_phone}` : undefined,
        "priceRange": Number.isFinite(minRent) ? `₹${minRent.toLocaleString('en-IN')}+` : undefined,
        "numberOfRooms": (available_rooms || []).length,
        "availableBedsCount": availableBeds,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": property.address || property.locality,
          "addressLocality": property.city,
          "addressRegion": property.state,
          "addressCountry": "IN",
        },
        "geo": (property.latitude && property.longitude) ? {
          "@type": "GeoCoordinates",
          "latitude": property.latitude,
          "longitude": property.longitude,
        } : undefined,
        "amenityFeature": Array.isArray(settings?.amenities) ? settings.amenities.map(a => ({
          "@type": "LocationFeatureSpecification",
          "name": a,
          "value": true,
        })) : undefined,
        "isAccessibleForFree": false,
        "offers": Number.isFinite(minRent) ? {
          "@type": "Offer",
          "price": minRent,
          "priceCurrency": "INR",
          "availability": availableBeds > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          "description": `Starting price per bed per month. ${availableBeds} bed${availableBeds === 1 ? '' : 's'} available.`,
        } : undefined,
      },
    ];

    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = `schema-listing-${property.id}`;
    // Strip keys whose values are undefined (Schema.org validators don't like nulls).
    el.textContent = JSON.stringify(schemas, (k, v) => (v === undefined ? undefined : v));
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, [property, settings, available_rooms, availableBeds, minRent]);

  if (loading) {
    return <div className="min-h-screen flex justify-center items-center"><div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;
  }
  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center p-6">
        <Home size={64} className="text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold">Listing not found</h2>
        <p className="text-gray-600 mt-2">This listing may have been removed or is no longer published.</p>
        <Link to="/listings" className="mt-6 text-blue-600 hover:underline">← Browse all listings</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageMeta
        title={`${property.name} — PG in ${property.city || 'India'}`}
        description={`${property.name} in ${property.city || 'India'} · ${availableBeds} beds available · ${rentText}. Transparent pricing, verified listing. Enquire via WhatsApp on RentKollect.`}
        ogImage={settings?.cover_image || property.images?.[0]}
      />
      <header className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-blue-700">
            <Home /> RentKollect
          </Link>
          <Link to="/listings" className="text-sm text-gray-600 hover:text-blue-600">← All listings</Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="public-listing-detail">
        {/* Hero */}
        <div className="aspect-[21/9] rounded-3xl overflow-hidden mb-6 bg-gradient-to-br from-blue-500 to-indigo-600 relative">
          {(settings.cover_image || property.images?.[0]) ? (
            <img src={settings.cover_image || property.images[0]} alt={property.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/90">
              <Home size={120} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h1 className="text-4xl font-bold text-gray-900">{property.name}</h1>
            {property.address && (
              <p className="text-gray-600 flex items-center gap-2 mt-2 text-lg">
                <MapPin size={18} /> {property.address}{property.city ? `, ${property.city}` : ''}
              </p>
            )}
            {settings.headline && <p className="text-xl text-gray-700 italic mt-4">"{settings.headline}"</p>}

            {settings.description && (
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-2">About this property</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{settings.description}</p>
              </div>
            )}

            {settings.highlights?.length > 0 && (
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Sparkles size={18} className="text-yellow-500" /> Highlights</h2>
                <div className="grid grid-cols-2 gap-2">
                  {settings.highlights.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white p-3 rounded-lg border border-gray-100">
                      <Check size={16} className="text-green-600" />
                      <span className="text-sm text-gray-800">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {property.amenities?.length > 0 && (
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-3">Amenities</h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((a, i) => (
                    <span key={i} className="text-xs bg-blue-50 text-blue-800 px-3 py-1.5 rounded-full">{a}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8">
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2"><Bed size={18} /> Available Rooms ({available_rooms.length})</h2>
              {available_rooms.length === 0 ? (
                <p className="text-gray-500">All rooms currently occupied.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {available_rooms.map(r => (
                    <div key={r.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      {r.photos && r.photos.length > 0 && (
                        <div className="flex gap-1 p-1 overflow-x-auto bg-gray-50" data-testid={`public-room-photos-${r.id}`}>
                          {r.photos.slice(0, 5).map((src, i) => (
                            <img key={i} src={src} alt="" className="w-24 h-20 object-cover rounded flex-shrink-0" />
                          ))}
                        </div>
                      )}
                      <div className="p-4">
                        <p className="font-semibold text-gray-900">Room {r.room_number}</p>
                        <div className="text-xs text-gray-600 flex items-center gap-3 mt-1">
                          <span>{r.room_type}</span>
                          <span>•</span>
                          <span>{r.total_beds} bed{r.total_beds > 1 ? 's' : ''}</span>
                          {r.photos && r.photos.length > 0 && (
                            <><span>•</span><span className="text-blue-600">📷 {r.photos.length}</span></>
                          )}
                        </div>
                        {(r.rent_per_bed || r.rent_amount) && (
                          <p className="text-xl font-bold text-blue-700 mt-2">₹{(r.rent_per_bed || r.rent_amount).toLocaleString()}/mo</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 sticky top-6">
              {settings.starting_rent && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500">Starting from</p>
                  <p className="text-3xl font-bold text-blue-700">₹{settings.starting_rent.toLocaleString()}<span className="text-sm text-gray-500">/month</span></p>
                </div>
              )}
              <button
                data-testid="inquiry-cta"
                onClick={() => setShowInquiry(true)}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 rounded-xl font-semibold shadow-lg transition-all"
              >
                Request Info
              </button>
              {settings.contact_phone && (
                <a href={`tel:${settings.contact_phone}`} className="mt-3 w-full flex items-center justify-center gap-2 border border-gray-300 py-2.5 rounded-xl font-medium hover:bg-gray-50">
                  <Phone size={16} /> Call
                </a>
              )}
              {settings.contact_whatsapp && (
                <a href={`https://wa.me/91${(settings.contact_whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="mt-3 w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-xl font-medium">
                  <MessageCircle size={16} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {showInquiry && (
        <InquiryModal
          propertyId={property.id}
          propertyName={property.name}
          onClose={() => setShowInquiry(false)}
        />
      )}
    </div>
  );
};

const InquiryModal = ({ propertyId, propertyName, onClose }) => {
  const [form, setForm] = useState({ name: '', phone: '', email: '', message: '', move_in_date: '', budget_max: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/listings/public/inquiry`, {
        property_id: propertyId,
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message || null,
        move_in_date: form.move_in_date || null,
        budget_max: form.budget_max ? parseFloat(form.budget_max) : null,
      });
      setSent(true);
    } catch {
      alert('Something went wrong. Please call the owner directly.');
    } finally { setSending(false); }
  };

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="bg-white rounded-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center mb-4">
            <Check size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Sent!</h2>
          <p className="text-gray-600 mb-6">Thank you. The property owner will contact you shortly.</p>
          <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="inquiry-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl max-w-md w-full p-6 space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold">Request Info</h2>
            <p className="text-xs text-gray-500">For {propertyName}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>
        <input data-testid="inquiry-name" required placeholder="Your Name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        <input data-testid="inquiry-phone" required placeholder="Phone *" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        <input data-testid="inquiry-email" type="email" placeholder="Email (optional)" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="inquiry-move-in" type="date" value={form.move_in_date} onChange={e => setForm({...form, move_in_date: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg" />
          <input data-testid="inquiry-budget" type="number" placeholder="Max Budget (₹)" value={form.budget_max} onChange={e => setForm({...form, budget_max: e.target.value})} className="px-3 py-2 border border-gray-300 rounded-lg" />
        </div>
        <textarea data-testid="inquiry-message" placeholder="Message (optional)" value={form.message} onChange={e => setForm({...form, message: e.target.value})} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        <button data-testid="inquiry-submit" type="submit" disabled={sending} className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2.5 rounded-lg font-medium">
          {sending ? 'Sending...' : 'Send Inquiry'}
        </button>
      </form>
    </div>
  );
};

export default PublicListingsIndex;
